import { useCallback, useEffect, useState } from 'react'

import { fabric } from 'fabric'
import { Canvas as CanvasType } from 'fabric/fabric-impl'

import styled from 'styled-components'
import { keyboard } from '../../input/keyboard'
import GameGlobals from '../Game/GameGlobals'
import GraphicObject from '../../logic/modules/GraphicObject'

const CanvasWrapper = styled.div`
	position: absolute;
	width: 100%;
	height: 100%;
`

export default function Canvas({ onCanvasCreated, canvasType }: { onCanvasCreated: (canvas: CanvasType) => void, canvasType: "physical" | "mana" }) {

	const [observerNode, setObserverNode] = useState<any>()
	const [canvas, setCanvas] = useState(null as any)

	const onMountRef = useCallback(node => {
		if (node) {
			const canvas = new fabric.Canvas(node, {
				selection: false,
				fireRightClick: true,
				fireMiddleClick: true,
				stopContextMenu: true,

			})

			// === Zoom logic ===
			canvas.on('mouse:wheel', function (opt: any) {
				if ((!Boolean(opt.target)) || (opt.target !== canvas.getActiveObject())) {
					var delta = opt.e.deltaY;
					var zoom = canvas.getZoom();
					zoom *= 0.999 ** delta;
					if (zoom > 1) zoom = 1;
					if (zoom < 0.01) zoom = 0.01;

					if (GameGlobals.viewportFollow && canvasType === 'physical') {
						canvas.setZoom(zoom)
						const ent = GameGlobals.selectedEntity
						if (ent && ent instanceof GraphicObject) {

							var vpt = canvas.viewportTransform;
							if (vpt) {

								vpt[4] = ((canvas.getWidth()) / 2) - ent.position.x * vpt[0];
								vpt[5] = ((canvas.getHeight()) / 2) - ent.position.y * vpt[0];

								ent.graphics?.setCoords()
								canvas.requestRenderAll();
							}
						}
					} else {
						canvas.zoomToPoint(new fabric.Point(opt.e.offsetX, opt.e.offsetY), zoom);
					}

					opt.e.preventDefault();
					opt.e.stopPropagation();
					return false;
				}
			});

			// === Pan logic ===
			canvas.on('mouse:down', function (this: any, opt: any) {
				var evt = opt.e;
				if (evt.button === 1 && ((canvasType === 'mana') || !GameGlobals.viewportFollow)) {
					this.isDragging = true;
					this.lastPosX = evt.clientX;
					this.lastPosY = evt.clientY;
					this.defaultCursor = 'grabbing'
					evt.preventDefault();
					evt.stopPropagation();
					return false;
				}
			});

			canvas.on('mouse:move', function (this: any, opt: any) {
				if (this.isDragging) {
					var e = opt.e;
					var vpt = this.viewportTransform;
					vpt[4] += e.clientX - this.lastPosX;
					vpt[5] += e.clientY - this.lastPosY;
					this.requestRenderAll();
					this.lastPosX = e.clientX;
					this.lastPosY = e.clientY;
					e.preventDefault();
					e.stopPropagation();
					return false;
				}
			});
			canvas.on('mouse:up', function (this: any, opt: any) {
				// on mouse up we want to recalculate new interaction
				// for all objects, so we call setViewportTransform
				this.setViewportTransform(this.viewportTransform);
				this.isDragging = false;
				this.defaultCursor = 'auto'
			});

			canvas.on('object:moving', function (options: any) {
				const gridSize = keyboard.shiftDown ? 10 : 40
				if (keyboard.ctrlDown) {
					options.target.set({
						left: Math.round(options.target.left / gridSize) * gridSize,
						top: Math.round(options.target.top / gridSize) * gridSize
					}).setCoords();
				}
			});

			if (canvasType === 'physical') {
				canvas.on('object:moving', function (options: any) {
					const target = options.target
					if (target && target.type === 'linkedgroup') {
						const obj = target.linkedObject

						if (obj) {
							if (GameGlobals.selectedEntity === obj && obj instanceof GraphicObject) {

								var vpt = canvas.viewportTransform;
								if (vpt) {

									vpt[4] = ((canvas.getWidth()) / 2) - obj.position.x * vpt[0];
									vpt[5] = ((canvas.getHeight()) / 2) - obj.position.y * vpt[0];

									obj.graphics?.setCoords()
									canvas.requestRenderAll();
								}
							}
						}
					}
				});
			}

			setCanvas(canvas)
			onCanvasCreated(canvas)
		}
	}, [onCanvasCreated, canvasType])

	const onObserverMount = useCallback((node) => {
		if (node && canvas) {
			setObserverNode(node)
		}
	}, [canvas])

	useEffect(() => {
		if (observerNode && canvas) {
			const observer = new ResizeObserver(entries => {
				const rect = entries[0].contentRect

				canvas.setWidth(Math.round(rect.width))
				canvas.setHeight(Math.round(rect.height))
				canvas.calcOffset()
			})

			observer.observe(observerNode)

			return () => {
				observer.disconnect()
			}
		}
	}, [observerNode, canvas])

	useEffect(() => {
		if (canvas) {
			return () => {
				canvas.dispose()
			}
		}
	}, [canvas])

	return (
		<CanvasWrapper ref={onObserverMount}>
			<canvas ref={onMountRef} />
		</CanvasWrapper>
	)
}
